import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type {
	ConfluenceTeamAdminControlsMode,
	useTeamPickerQuery$data,
	useTeamPickerQuery$variables,
} from './__generated__/useTeamPickerQuery.graphql.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';

const useTeamPickerQuery = graphql`
	query useTeamPickerQuery {
		confluence {
			siteConfiguration {
				teamAdminControlsSettings {
					teamAdminControlsMode
				}
			}
		}
	}
`;

export interface TeamPickerProps {
	includeTeams: boolean;
	verifiedTeams: boolean;
	includeTeamsUpdates: boolean;
}

function modeToPickerProps(
	mode: ConfluenceTeamAdminControlsMode | null | undefined,
): TeamPickerProps {
	switch (mode) {
		case 'ALL_TEAMS':
			return { includeTeams: true, verifiedTeams: false, includeTeamsUpdates: true };
		case 'VERIFIED_TEAMS':
			return { includeTeams: true, verifiedTeams: true, includeTeamsUpdates: true };
		case null:
			return { includeTeams: false, verifiedTeams: false, includeTeamsUpdates: false };
		case undefined:
			return { includeTeams: false, verifiedTeams: false, includeTeamsUpdates: false };
		default:
			return { includeTeams: false, verifiedTeams: false, includeTeamsUpdates: true };
	}
}

export const useTeamPicker = (): { pickerProps: TeamPickerProps } => {
	const { data } = useQueryInterop<useTeamPickerQuery$data, useTeamPickerQuery$variables>(
		useTeamPickerQuery,
		{
			onError: (error) => {
				markErrorAsHandled(error);
			},
			skip: !expVal('confluence_frontend_perms_teams_ga_fast_follow', 'isEnabled', false),
		},
	);

	const pickerProps = useMemo(() => {
		if (fg('confluence_frontend_perms_exp_teams_as_principals')) {
			return { includeTeams: true, verifiedTeams: false, includeTeamsUpdates: true };
		}
		const mode =
			data?.confluence?.siteConfiguration?.teamAdminControlsSettings?.teamAdminControlsMode;

		return modeToPickerProps(mode);
	}, [data?.confluence?.siteConfiguration?.teamAdminControlsSettings?.teamAdminControlsMode]);

	return { pickerProps };
};
